import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ImageIcon, MoreVertical, ChevronDown, ChevronRight, AlertCircle } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { ImageZoomModal } from '@/components/order/ImageZoomModal';
import OrderPreviewMatrix from '@/components/order/OrderPreviewMatrix';
import { UkrposhtaTrackingDialog } from '@/components/order/UkrposhtaTrackingDialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  fetchOrders,
  fetchOrder,
  deleteOrder,
  type Order,
  type OrderListItem,
  type OrderStatus,
} from '@/services/orderService';
import { analytics } from '@/services/analytics';
import { logging } from '@/services/logging';
import { getSignedUrlForFile, getUploadMetadata, uploadFile } from '@/services/uploadService';
import { useAppDispatch } from '@/app/hooks';
import { completeOrderFulfillment } from '@/features/order/orderSlice';
import { loadWalletSummary } from '@/features/wallet/walletSlice';
import { supervisorService } from '@/services/supervisorService';

interface OrderListCanvasProps {
  readonly initialStatuses?: ReadonlyArray<OrderStatus>;
  readonly initialOrders?: ReadonlyArray<OrderListItem & { readonly itemCount?: number | null }>;
  readonly initialCursor?: string | null;
  readonly title?: string;
  readonly onEditOrder?: (orderId: string) => void;
  readonly onDeleteOrder?: (orderId: string) => void;
  readonly onPayOrder?: (orderId: string) => void;
}

interface UseOrderListResult {
  readonly orders: ReadonlyArray<OrderListItemWithCounts>;
  readonly isLoading: boolean;
  readonly isLoadingMore: boolean;
  readonly nextCursor: string | null;
  readonly error: string | null;
  readonly statuses: ReadonlyArray<OrderStatus>;
  readonly clearStatuses: () => void;
  readonly setStatuses: (values: ReadonlyArray<OrderStatus>) => void;
  readonly loadMore: () => void;
  readonly reload: () => void;
  readonly hasFetched: boolean;
}

type LoadMode = 'replace' | 'append';
type PreviewMeta = {
  widthIn: number | null;
  heightIn: number | null;
  marginIn: number | null;
  galleryWrapIn: number | null;
  originalWidthPx: number | null;
  originalHeightPx: number | null;
};

const useOrderList = (
  initialStatuses: ReadonlyArray<OrderStatus> = [],
  initialOrders: ReadonlyArray<OrderListItemWithCounts> = [],
  initialCursor: string | null = null,
): UseOrderListResult => {
  const [orders, setOrders] = useState<ReadonlyArray<OrderListItemWithCounts>>(initialOrders);
  const [statuses, setStatuses] = useState<ReadonlyArray<OrderStatus>>(initialStatuses);
  const [nextCursor, setNextCursor] = useState<string | null>(initialCursor);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState<boolean>(initialOrders.length > 0);

  useEffect(() => {
    setStatuses(initialStatuses);
  }, [initialStatuses]);

  useEffect(() => {
    setOrders(initialOrders);
    setNextCursor(initialCursor);
    setHasFetched(initialOrders.length > 0);
  }, [initialOrders, initialCursor]);

  const fetchOrdersPage = useCallback(
    async (mode: LoadMode, cursor?: string | null) => {
      const isAppend = mode === 'append';
      setError(null);
      if (isAppend) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }
      try {
        const payload = await fetchOrders({
          statuses: statuses.length > 0 ? statuses : undefined,
          cursor: isAppend ? cursor ?? undefined : undefined,
          limit: 20,
        });
        setNextCursor(payload.nextCursor ?? null);
        setOrders((prev) => (isAppend ? [...prev, ...payload.orders] : payload.orders));
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
      } finally {
        if (isAppend) {
          setIsLoadingMore(false);
        } else {
          setIsLoading(false);
        }
        setHasFetched(true);
      }
    },
    [statuses],
  );

  const reload = useCallback(() => fetchOrdersPage('replace'), [fetchOrdersPage]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const clearStatuses = useCallback(() => {
    setStatuses([]);
  }, []);

  const replaceStatuses = useCallback((values: ReadonlyArray<OrderStatus>) => {
    setStatuses([...values]);
  }, []);

  const loadMore = useCallback(() => {
    if (!nextCursor || isLoadingMore) {
      return;
    }
    void fetchOrdersPage('append', nextCursor);
  }, [fetchOrdersPage, isLoadingMore, nextCursor]);

  return {
    orders,
    isLoading,
    isLoadingMore,
    nextCursor,
    error,
    statuses,
    clearStatuses,
    setStatuses: replaceStatuses,
    loadMore,
    reload,
    hasFetched,
  };
};

const renderDestination = (
  fulfillmentMode: OrderListItem['fulfillmentMode'] | undefined,
  destination: OrderListItem['destination'],
  fallback: string,
  pickupLabel: string,
): string => {
  if (fulfillmentMode === 'PICKUP') {
    return pickupLabel;
  }
  if (!destination) {
    return fallback;
  }
  const primaryParts = [destination.city, destination.postalCode].filter(Boolean);
  if (primaryParts.length > 0) {
    return primaryParts.join(', ');
  }
  const fallbackParts = [destination.region, destination.countryCode].filter(Boolean);
  return fallbackParts.length > 0 ? fallbackParts.join(', ') : fallback;
};

type OrderListItemWithCounts = OrderListItem & { readonly itemCount?: number | null };

type FilterGroup = {
  readonly id: string;
  readonly statuses: ReadonlyArray<OrderStatus>;
  readonly label: string;
};

export const OrderListCanvas = ({
  initialStatuses,
  initialOrders,
  initialCursor,
  title,
  onDeleteOrder = () => {},
  onPayOrder = () => {},
}: OrderListCanvasProps) => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const resolvedInitialStatuses = useMemo<ReadonlyArray<OrderStatus>>(
    () => (initialStatuses ? [...initialStatuses] : []),
    [initialStatuses],
  );
  const resolvedInitialOrders = useMemo<ReadonlyArray<OrderListItemWithCounts>>(
    () => (initialOrders ? [...initialOrders] : []),
    [initialOrders],
  );
  const resolvedInitialCursor = initialCursor ?? null;
  const {
    orders,
    isLoading,
    isLoadingMore,
    nextCursor,
    error,
    statuses,
    clearStatuses,
    setStatuses,
    loadMore,
    reload,
    hasFetched,
  } = useOrderList(resolvedInitialStatuses, resolvedInitialOrders, resolvedInitialCursor);

  const locale = i18n.language || navigator.language;

  const filterGroups = useMemo<ReadonlyArray<FilterGroup>>(
    () => [
      {
        id: 'new',
        statuses: ['NEW', 'PAID'],
        label: t('orders.filters.group.new', 'New'),
      },
      {
        id: 'inProcess',
        statuses: ['IN_PRODUCTION', 'PRINTED'],
        label: t('orders.filters.group.inProcess', 'In process'),
      },
      {
        id: 'inDelivery',
        statuses: ['IN_DELIVERY'],
        label: t('orders.filters.group.inDelivery', 'In delivery'),
      },
      {
        id: 'other',
        statuses: ['DELIVERED', 'RETURNED', 'CANCELLED'],
        label: t('orders.filters.group.other', 'Other'),
      },
    ],
    [t],
  );

  const applyFilterGroup = useCallback(
    (group: FilterGroup) => {
      analytics.track('button: ui.order_list_filter_apply', {
        groupId: group.id,
        statuses: group.statuses,
      });
      setStatuses(group.statuses);
    },
    [setStatuses],
  );

  const isGroupActive = useCallback(
    (group: FilterGroup) =>
      statuses.length === group.statuses.length &&
      group.statuses.every((status) => statuses.includes(status)),
    [statuses],
  );

  const activeGroup = useMemo(
    () => filterGroups.find((group) => isGroupActive(group)),
    [filterGroups, isGroupActive],
  );

  const formatStatusLabel = useCallback(
    (status: OrderStatus) => t(`orders.status.${status}`, status),
    [t],
  );

  const statusesForDisplay = useMemo<ReadonlyArray<OrderStatus>>(
    () => (activeGroup ? [...activeGroup.statuses] : [...statuses]),
    [activeGroup, statuses],
  );

  const statusLabelText = useMemo(
    () => statusesForDisplay.map((status) => formatStatusLabel(status)).join(', '),
    [formatStatusLabel, statusesForDisplay],
  );

  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewMeta, setPreviewMeta] = useState<PreviewMeta | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [isOrderIdDialogOpen, setIsOrderIdDialogOpen] = useState<boolean>(false);
  const [orderIdPendingDeletion, setOrderIdPendingDeletion] = useState<string | null>(null);
  const [orderStatusPendingDeletion, setOrderStatusPendingDeletion] = useState<OrderStatus | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [openMenuOrderId, setOpenMenuOrderId] = useState<string | null>(null);
  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<string>>(new Set());
  const [detailedOrders, setDetailedOrders] = useState<Record<string, Order>>({});
  const [loadingDetails, setLoadingDetails] = useState<Set<string>>(new Set());
  const [trackingDialogOrder, setTrackingDialogOrder] = useState<{ orderId: string; trackingNumber: string | null } | null>(null);
  const [itemPreviews, setItemPreviews] = useState<Record<string, string | null>>({});
  const [reuploadTarget, setReuploadTarget] = useState<{ orderId: string; itemId: string } | null>(null);
  const [reuploadingItemId, setReuploadingItemId] = useState<string | null>(null);
  const previewRequestsRef = useRef<Set<string>>(new Set());
  const reuploadInputRef = useRef<HTMLInputElement | null>(null);

  const toggleOrderExpansion = useCallback(
    async (orderId: string) => {
      const newExpandedIds = new Set(expandedOrderIds);
      if (newExpandedIds.has(orderId)) {
        newExpandedIds.delete(orderId);
      } else {
        newExpandedIds.add(orderId);
        // Fetch details if not already loaded
        if (!detailedOrders[orderId] && !loadingDetails.has(orderId)) {
          setLoadingDetails((prev) => new Set(prev).add(orderId));
          try {
            const orderDetails = await fetchOrder(orderId);
            setDetailedOrders((prev) => ({ ...prev, [orderId]: orderDetails }));
          } catch (err) {
            logging.error('orders.fetch_details_failed', { orderId, error: err });
            const responseMessage = typeof (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message === 'string'
              ? (err as { response?: { data?: { message?: string } } }).response?.data?.message?.trim() ?? ''
              : '';
            const message = responseMessage || (err instanceof Error
              ? err.message
              : t('orders.fetchDetailsErrorDescription', 'Could not load order details right now. Please try again.'));
            toast({
              title: t('orders.fetchDetailsErrorTitle', 'Failed to load order details'),
              description: message,
              variant: 'destructive',
            });
            newExpandedIds.delete(orderId);
          } finally {
            setLoadingDetails((prev) => {
              const newLoading = new Set(prev);
              newLoading.delete(orderId);
              return newLoading;
            });
          }
        }
      }
      setExpandedOrderIds(newExpandedIds);
      analytics.track('button: ui.order_list_toggle_expansion', { orderId, isExpanded: newExpandedIds.has(orderId) });
    },
    [expandedOrderIds, detailedOrders, loadingDetails, t, toast],
  );

  const formatDate = useCallback(
    (value: string) =>
      new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(value)),
    [locale],
  );

  const formatCurrency = useCallback(
    (amount: number, currency: string) =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format(amount),
    [locale],
  );

  const formatInches = useCallback(
    (value: number | null | undefined) => {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        return '?';
      }

      return `${new Intl.NumberFormat(locale, {
        maximumFractionDigits: 2,
      }).format(value)}"`;
    },
    [locale],
  );

  const renderTotalAmount = useCallback(
    (entry: OrderListItem) => {
      const amount = typeof entry.totalAmount?.value === 'number' ? entry.totalAmount.value : null;
      const currency = typeof entry.totalAmount?.currency === 'string' ? entry.totalAmount.currency : null;

      if (amount != null && Number.isFinite(amount) && currency && currency.length > 0) {
        return formatCurrency(amount, currency);
      }

      return t('orders.table.totalFallback', '—');
    },
    [formatCurrency, t],
  );

  const getStatusBadgeClassName = useCallback((status: OrderStatus) => {
    switch (status) {
      case 'NEW':
      case 'PAID':
        return 'border-amber-300/70 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100';
      case 'IN_PRODUCTION':
      case 'PRINTED':
        return 'border-sky-300/70 bg-sky-50 text-sky-900 dark:border-sky-800 dark:bg-sky-950/30 dark:text-sky-100';
      case 'IN_DELIVERY':
        return 'border-violet-300/70 bg-violet-50 text-violet-900 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-100';
      case 'DELIVERED':
        return 'border-emerald-300/70 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-100';
      case 'CANCELLED':
      case 'RETURNED':
        return 'border-slate-300/70 bg-slate-100 text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200';
      default:
        return 'border-border bg-muted text-muted-foreground';
    }
  }, []);

  const showEmpty = hasFetched && !isLoading && !isLoadingMore && orders.length === 0 && !error;

  const showInitialSpinner = isLoading && !hasFetched;
  const showLoadingOverlay = isLoading && hasFetched;
  const hasActiveFilter = statuses.length > 0;

  const handleClearStatuses = useCallback(() => {
    analytics.track('button: ui.order_list_filter_clear');
    clearStatuses();
  }, [clearStatuses]);

  const handleLoadMore = useCallback(() => {
    analytics.track('button: ui.order_list_load_more');
    loadMore();
  }, [loadMore]);

  const closePreview = useCallback(() => {
    setIsPreviewOpen(false);
    setPreviewImageUrl(null);
    setPreviewMeta(null);
    analytics.track('ImageZoomModal: ui.order_list_preview_close');
  }, []);

  const closeTrackingDialog = useCallback(() => {
    setTrackingDialogOrder(null);
  }, []);

  const openTrackingDialog = useCallback((order: OrderListItemWithCounts) => {
    setTrackingDialogOrder({
      orderId: order.id,
      trackingNumber: order.trackingNumber ?? null,
    });
    analytics.track('button: ui.order_list_tracking_open', { orderId: order.id });
  }, []);

  const openDetailPreview = useCallback(
    (orderId: string, item: Order['orderItems'][number]) => {
      const fileId = item.uploadedFileId ?? null;
      if (!fileId) {
        return;
      }
      const url = itemPreviews[fileId];
      if (!url) {
        return;
      }
      setPreviewImageUrl(url);
      setPreviewMeta({
        widthIn: item.widthIn ?? null,
        heightIn: item.heightIn ?? null,
        marginIn: item.marginIn ?? null,
        galleryWrapIn: item.galleryWrapIn ?? null,
        originalWidthPx: item.originalWidthPx ?? null,
        originalHeightPx: item.originalHeightPx ?? null,
      });
      setIsPreviewOpen(true);
      analytics.track('button: ui.order_list_detail_preview_open', { orderId, fileId });

      void (async () => {
        try {
          const metadata = await getUploadMetadata(fileId);
          if (!metadata) {
            return;
          }

          setPreviewMeta(prev => {
            if (!prev) {
              return prev;
            }

            const next = { ...prev };

            if ((!prev.originalWidthPx || prev.originalWidthPx <= 0) && typeof metadata.width === 'number' && metadata.width > 0) {
              next.originalWidthPx = metadata.width;
            }

            if ((!prev.originalHeightPx || prev.originalHeightPx <= 0) && typeof metadata.height === 'number' && metadata.height > 0) {
              next.originalHeightPx = metadata.height;
            }

            if ((!prev.widthIn || prev.widthIn <= 0) && typeof metadata.widthIn === 'number' && metadata.widthIn > 0) {
              next.widthIn = metadata.widthIn;
            }

            if ((!prev.heightIn || prev.heightIn <= 0) && typeof metadata.heightIn === 'number' && metadata.heightIn > 0) {
              next.heightIn = metadata.heightIn;
            }

            return next;
          });
        } catch (error) {
          logging.error('Failed to load upload metadata for preview', { error, fileId });
        }
      })();
    },
    [itemPreviews],
  );

  const openOrderIdDialog = useCallback((displayCode: string) => {
    setSelectedOrderId(displayCode);
    setIsOrderIdDialogOpen(true);
    analytics.track('dropdown: ui.order_list_show_id_open', { orderId: displayCode });
  }, []);

  const closeOrderIdDialog = useCallback(() => {
    setIsOrderIdDialogOpen(false);
    setSelectedOrderId(null);
    analytics.track('dialog: ui.order_list_show_id_close');
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setOrderIdPendingDeletion(null);
    setIsDeleting(false);
    setOpenMenuOrderId(null);
    analytics.track('dialog: ui.order_list_delete_cancel');
  }, []);

  const handleCopyOrderId = useCallback(async () => {
    if (!selectedOrderId) {
      return;
    }
    try {
      await navigator.clipboard.writeText(selectedOrderId);
      analytics.track('button: ui.order_list_copy_id', { orderId: selectedOrderId });
      closeOrderIdDialog();
    } catch (error) {
      logging.error('Failed to copy order id', { error });
    }
  }, [closeOrderIdDialog, selectedOrderId]);

  useEffect(() => {
    let cancelled = false;
    const fileIds = new Set<string>();
    expandedOrderIds.forEach(orderId => {
      const order = detailedOrders[orderId];
      if (!order) {
        return;
      }
      order.orderItems?.forEach(item => {
        if (item.uploadedFileId) {
          fileIds.add(item.uploadedFileId);
        }
      });
    });

    fileIds.forEach(async fileId => {
      if (previewRequestsRef.current.has(fileId)) {
        return;
      }
      previewRequestsRef.current.add(fileId);
      try {
        const { thumbnailSignedUrl, signedUrl } = await getSignedUrlForFile(fileId);
        if (cancelled) {
          return;
        }
        setItemPreviews(prev => ({
          ...prev,
          [fileId]: thumbnailSignedUrl ?? signedUrl ?? null,
        }));
      } catch (error) {
        if (cancelled) {
          return;
        }
        setItemPreviews(prev => ({
          ...prev,
          [fileId]: null,
        }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [detailedOrders, expandedOrderIds]);

  const handleConfirmDelete = useCallback(async () => {
    if (!orderIdPendingDeletion) {
      return;
    }
    setIsDeleting(true);
    const pendingStatus = orderStatusPendingDeletion;
    try {
      analytics.track('button: ui.order_list_delete_confirm', { orderId: orderIdPendingDeletion });
      await deleteOrder(orderIdPendingDeletion);
      await reload();
      onDeleteOrder(orderIdPendingDeletion);
      if (pendingStatus) {
        const normalizedStatus = pendingStatus.trim().toUpperCase();
        if (normalizedStatus === 'NEW' || normalizedStatus === 'PAID') {
          dispatch(completeOrderFulfillment());
          supervisorService.send({
            type: 'ORDER.CLEAR_FOR_ORDER',
            orderId: orderIdPendingDeletion,
          });
        }
        if (normalizedStatus === 'PAID') {
          dispatch(loadWalletSummary());
        }
      }
      closeDeleteDialog();
    } catch (error) {
      logging.error('Failed to delete order', { error, orderId: orderIdPendingDeletion });
      setIsDeleting(false);
    }
  }, [closeDeleteDialog, dispatch, onDeleteOrder, orderIdPendingDeletion, orderStatusPendingDeletion, reload]);

  const handleRequestReupload = useCallback((orderId: string, itemId: string) => {
    setReuploadTarget({ orderId, itemId });
    reuploadInputRef.current?.click();
  }, []);

  const handleReuploadFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const target = reuploadTarget;
    event.target.value = '';

    if (!file || !target) {
      return;
    }

    setReuploadingItemId(target.itemId);
    try {
      await uploadFile(file, { reuploadOrderItemId: target.itemId });
      const refreshedOrder = await fetchOrder(target.orderId);
      setDetailedOrders((prev) => ({
        ...prev,
        [target.orderId]: refreshedOrder,
      }));
      await reload();
      toast({
        title: t('orders.reupload.successTitle', 'Replacement image uploaded'),
        description: t('orders.reupload.successDescription', 'The new file was attached to this order item.'),
      });
    } catch (error) {
      logging.error('orders.reupload_failed', { error, orderId: target.orderId, orderItemId: target.itemId });
      const responseMessage = typeof (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message === 'string'
        ? (error as { response?: { data?: { message?: string } } }).response?.data?.message?.trim() ?? ''
        : '';
      const message = responseMessage || (error instanceof Error
        ? error.message
        : t('orders.reupload.errorDescription', 'The image could not be uploaded. Please try again.'));
      toast({
        title: t('orders.reupload.errorTitle', 'Could not upload replacement image'),
        description: message,
        variant: 'destructive',
      });
    } finally {
      setReuploadingItemId((current) => (current === target.itemId ? null : current));
      setReuploadTarget(null);
    }
  }, [reload, reuploadTarget, t, toast]);

  const handleNewOrder = useCallback(() => {
    analytics.track('button: ui.order_list_new_order');
    supervisorService.send({ type: 'UI.GOTO_STEP', step: 'upload' });
  }, []);

  return (
    <div className="w-full h-full overflow-y-auto">
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold text-foreground">
              {title ?? t('orders.title', 'Orders')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t(
                'orders.subtitle',
                'Review your orders and track their progress in real time.',
              )}
            </p>
          </div>
          <Button onClick={handleNewOrder}>
            {t('orders.newOrder', 'New order')}
          </Button>
        </div>

        <div className="rounded-lg border bg-background p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant={hasActiveFilter ? 'outline' : 'default'}
              size="sm"
              onClick={handleClearStatuses}
            >
              {t('orders.filters.group.all', 'All orders')}
            </Button>
            {filterGroups.map((group) => (
              <Button
                key={group.id}
                size="sm"
                variant={isGroupActive(group) ? 'default' : 'outline'}
                onClick={() => applyFilterGroup(group)}
              >
                {group.label}
              </Button>
            ))}
          </div>
          {hasActiveFilter && statusLabelText ? (
            <p className="text-xs text-muted-foreground">
              {t('orders.filters.activeGroupStatuses', {
                defaultValue: 'Showing orders with {{statuses}} statuses.',
                statuses: statusLabelText,
              })}
            </p>
          ) : null}
        </div>

        {error ? (
          <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-destructive">
            {t('orders.error', 'We could not load your orders. Please try again.')}
          </div>
        ) : null}

        <input
          ref={reuploadInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleReuploadFileChange}
        />

        <div className="relative space-y-4">
          {showInitialSpinner ? (
            <div className="rounded-2xl border bg-background px-4 py-10">
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                {t('orders.loading', 'Loading orders...')}
              </div>
            </div>
          ) : (
            orders.map((order, index) => {
              const isExpanded = expandedOrderIds.has(order.id);
              const isDeletable =
                order.status === 'NEW' || order.status === 'CANCELLED' || order.status === 'PAID';
              const orderDetail = detailedOrders[order.id];
              const detailPreviewSources = orderDetail?.orderItems
                ? orderDetail.orderItems
                    .map((item) => (item.uploadedFileId ? itemPreviews[item.uploadedFileId] ?? null : null))
                    .filter((url): url is string => Boolean(url))
                : [];
              const listPreviewSources = order.previewUrls?.length
                ? [...order.previewUrls]
                : [];
              const fallbackThumbnail =
                listPreviewSources.length === 0 && detailPreviewSources.length === 0 && order.thumbnailUrl
                  ? [order.thumbnailUrl]
                  : [];
              const preferredSources = detailPreviewSources.length > 0
                ? detailPreviewSources
                : [...listPreviewSources, ...fallbackThumbnail];
              const baseSources = Array.from(new Set(preferredSources));
              const showUkrposhtaTrackingLink = order.status === 'IN_DELIVERY'
                && order.shippingProvider === 'UKRPOSHTA'
                && typeof order.trackingNumber === 'string'
                && order.trackingNumber.trim().length > 0;
              const adminComments = Array.isArray(order.adminComments) ? order.adminComments : [];
              const itemCount = typeof order.itemCount === 'number' && Number.isFinite(order.itemCount)
                ? Math.max(0, Math.round(order.itemCount))
                : orderDetail?.orderItems?.length ?? 0;

              return (
                <section
                  key={order.id}
                  className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="flex flex-col gap-4 p-4 sm:p-5">
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                        <button
                          type="button"
                          onClick={() => toggleOrderExpansion(order.id)}
                          className="shrink-0 self-start rounded-2xl border border-border bg-muted/30 p-2 transition hover:bg-muted/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          aria-label={isExpanded
                            ? t('orders.actions.collapse', 'Collapse')
                            : t('orders.actions.expand', 'Expand')}
                        >
                          <OrderPreviewMatrix
                            previewUrls={baseSources}
                            itemCount={order.itemCount}
                            alt={t('orders.table.previewAlt', 'Order preview')}
                            ariaLabel={t('orders.table.previewToggle', 'Toggle order preview details')}
                          />
                        </button>

                        <div className="min-w-0 flex-1 space-y-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 space-y-2">
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className="truncate text-lg font-semibold text-foreground">
                                  {order.displayCode}
                                </h3>
                                <span className="inline-flex items-center rounded-full border border-border/70 bg-muted/50 px-2.5 py-1 text-xs font-medium text-muted-foreground">
                                  #{index + 1}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => toggleOrderExpansion(order.id)}
                                  className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary shadow-sm transition hover:border-primary/50 hover:bg-primary/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                                  aria-label={isExpanded
                                    ? t('orders.actions.collapse', 'Collapse')
                                    : t('orders.actions.expand', 'Expand')}
                                >
                                  <span>{itemCount} {t('orders.details.orderItems', 'Order items')}</span>
                                  {isExpanded ? (
                                    <ChevronDown className="size-3.5" aria-hidden />
                                  ) : (
                                    <ChevronRight className="size-3.5" aria-hidden />
                                  )}
                                </button>
                                <span className={`hidden sm:inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${getStatusBadgeClassName(order.status)}`}>
                                  {t(`orders.status.${order.status}`, order.status)}
                                </span>
                                <p className="text-sm text-muted-foreground sm:ml-2">
                                  {t('orders.table.created', 'Created')}: {formatDate(order.createdAt)}
                                </p>
                              </div>
                            </div>

                            <DropdownMenu
                              open={openMenuOrderId === order.id}
                              onOpenChange={(open) => {
                                setOpenMenuOrderId(open ? order.id : null);
                              }}
                            >
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0">
                                  <MoreVertical className="size-4" />
                                  <span className="sr-only">
                                    {t('orders.table.openActions', 'Open actions')}
                                  </span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="min-w-[10rem] text-right">
                                <DropdownMenuItem
                                  className="justify-end"
                                  disabled
                                  onSelect={(event) => {
                                    event.preventDefault();
                                  }}
                                >
                                  {t('orders.actions.edit', 'Edit')}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="justify-end"
                                  disabled={!isDeletable}
                                  onSelect={(event) => {
                                    event.preventDefault();
                                    if (!isDeletable) {
                                      return;
                                    }
                                    analytics.track('dropdown: ui.order_list_action_delete_request', { orderId: order.id });
                                    setOrderIdPendingDeletion(order.id);
                                    setOrderStatusPendingDeletion(order.status);
                                    setOpenMenuOrderId(null);
                                  }}
                                >
                                  {t('orders.actions.delete', 'Delete')}
                                </DropdownMenuItem>
                                {order.status === 'NEW' ? (
                                  <DropdownMenuItem
                                    className="justify-end"
                                    onSelect={(event) => {
                                      event.preventDefault();
                                      setOpenMenuOrderId(null);
                                      analytics.track('dropdown: ui.order_list_action_pay', { orderId: order.id });
                                      onPayOrder(order.id);
                                    }}
                                  >
                                    {t('orders.actions.pay', 'Pay')}
                                  </DropdownMenuItem>
                                ) : null}
                                <DropdownMenuItem
                                  className="justify-end"
                                  onSelect={(event) => {
                                    event.preventDefault();
                                    setOpenMenuOrderId(null);
                                    analytics.track('dropdown: ui.order_list_action_show_id', { orderId: order.id });
                                    openOrderIdDialog(order.displayCode);
                                  }}
                                >
                                  {t('orders.actions.showId', 'Show order ID')}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold sm:hidden ${getStatusBadgeClassName(order.status)}`}>
                              {t(`orders.status.${order.status}`, order.status)}
                            </span>
                            {showUkrposhtaTrackingLink ? (
                              <Button size="sm" variant="outline" onClick={() => openTrackingDialog(order)}>
                                {t('orders.actions.trackShipment', 'Track shipment')}
                              </Button>
                            ) : null}
                            {adminComments.length > 0 ? (
                              <TooltipProvider delayDuration={150}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      className="inline-flex items-center gap-1 rounded-full border border-amber-300/70 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100"
                                    >
                                      <AlertCircle className="size-4" />
                                      <span>{t('orders.adminComment.label', 'Admin comment')}</span>
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-sm text-left">
                                    <div className="space-y-2">
                                      <p className="text-xs font-semibold">
                                        {t('orders.adminComment.tooltipTitle', 'Administrator comments about your image')}
                                      </p>
                                      {adminComments.map((comment, commentIndex) => (
                                        <p key={comment.itemId + ':' + comment.requestedAt + ':' + String(commentIndex)} className="text-xs leading-relaxed">
                                          {comment.message}
                                        </p>
                                      ))}
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : null}
                          </div>

                          <div className="grid gap-3 sm:grid-cols-2">
                            <div className="rounded-xl border border-border/70 bg-muted/20 px-3 py-2">
                              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                                {t('orders.table.destination', 'Destination')}
                              </p>
                              <p className="mt-1 text-sm font-medium text-foreground">
                                {renderDestination(
                                  order.fulfillmentMode,
                                  order.destination,
                                  t('orders.table.destinationFallback', '—'),
                                  t('orders.table.pickup', 'Self pickup'),
                                )}
                              </p>
                            </div>
                            <div className="rounded-xl border border-border/70 bg-muted/20 px-3 py-2">
                              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                                {t('orders.table.total', 'Total')}
                              </p>
                              <p className="mt-1 text-sm font-semibold text-foreground">
                                {renderTotalAmount(order)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {isExpanded ? (
                      <div className="rounded-2xl border border-border/70 bg-muted/10">
                        {loadingDetails.has(order.id) ? (
                          <div className="flex items-center justify-center gap-2 p-6 text-muted-foreground">
                            <Loader2 className="size-4 animate-spin" />
                            <span>{t('orders.loadingDetails', 'Loading details...')}</span>
                          </div>
                        ) : detailedOrders[order.id] ? (
                          <div className="space-y-4 p-4">
                            <div className="flex items-center">
                              <span className="rounded-full border border-border/60 bg-background px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                {t('orders.details.orderItems', 'Order items')}
                              </span>
                            </div>
                            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                              {detailedOrders[order.id].orderItems.map((item) => {
                                const itemAdminComments = adminComments.filter((comment) => comment.itemId === item.id);
                                const canReupload = item.status === 'REUPLOAD_IMAGE';
                                const isUploadingReplacement = reuploadingItemId === item.id;
                                const itemQuantity = typeof item.quantity === 'number' && Number.isFinite(item.quantity)
                                  ? Math.max(1, Math.min(99, Math.round(item.quantity)))
                                  : 1;

                                return (
                                  <div
                                    key={item.id}
                                    className="rounded-2xl border border-dashed border-border/80 bg-background/90 p-3 shadow-sm"
                                  >
                                    <div className="flex flex-col gap-3 sm:flex-row">
                                      <button
                                        type="button"
                                        onClick={() => openDetailPreview(order.id, item)}
                                        disabled={!item.uploadedFileId || !itemPreviews[item.uploadedFileId]}
                                        className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl border bg-muted text-muted-foreground transition-transform disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 self-start"
                                        aria-label={t('orders.table.previewOpen', 'Open preview in modal')}
                                      >
                                        {item.uploadedFileId && itemPreviews[item.uploadedFileId] ? (
                                          <img
                                            src={itemPreviews[item.uploadedFileId] ?? undefined}
                                            alt={t('orders.table.previewAlt', 'Order preview')}
                                            className="h-full w-full object-cover"
                                          />
                                        ) : (
                                          <ImageIcon className="size-6 text-muted-foreground" />
                                        )}
                                      </button>
                                      <div className="min-w-0 flex-1 space-y-3">
                                        <div className="space-y-1">
                                          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                                            {t('orders.details.paper', 'Paper')}
                                          </p>
                                          <p className="truncate text-sm font-semibold text-foreground">
                                            {item.paperTypeId ?? t('orders.details.notAvailable', 'N/A')}
                                          </p>
                                        </div>

                                        <div className="grid gap-2 sm:grid-cols-2">
                                          <div className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2">
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                                              {t('orders.table.size', 'Size')}
                                            </p>
                                            <p className="mt-1 text-xs leading-5 text-foreground">
                                              {formatInches(item.widthIn)} × {formatInches(item.heightIn)}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                              {typeof item.marginIn === 'number' && item.marginIn > 0
                                                ? `${t('orders.table.margin', 'Margin')}: ${formatInches(item.marginIn)}`
                                                : typeof item.galleryWrapIn === 'number' && item.galleryWrapIn > 0
                                                  ? `${t('orders.table.galleryWrap', 'Gallery Wrap')}: ${formatInches(item.galleryWrapIn)}`
                                                  : `${t('orders.table.marginGalleryWrap', 'Margin / Gallery Wrap')}: ${t('orders.table.noEdgeTreatment', '—')}`}
                                            </p>
                                          </div>
                                          <div className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2">
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                                              {t('orders.details.price', 'Price')}
                                            </p>
                                            <p className="mt-1 text-sm font-semibold text-foreground">
                                              {item.priceUsd != null ? formatCurrency(item.priceUsd, 'USD') : t('orders.details.notPriced', 'Not priced')}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                              {t('orders.details.quantity', 'Qty')}: {itemQuantity}
                                            </p>
                                          </div>
                                        </div>

                                        {(itemAdminComments.length > 0 || canReupload) ? (
                                          <div className="flex flex-col items-start gap-2 pt-1 sm:flex-row sm:flex-wrap sm:items-center">
                                            {itemAdminComments.length > 0 ? (
                                              <TooltipProvider delayDuration={150}>
                                                <Tooltip>
                                                  <TooltipTrigger asChild>
                                                    <button
                                                      type="button"
                                                      className="inline-flex max-w-full items-center gap-1 rounded-full border border-amber-300/70 bg-amber-50 px-3 py-1 text-left text-xs font-medium text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100"
                                                    >
                                                      <AlertCircle className="size-4 shrink-0" />
                                                      <span>{t('orders.adminComment.label', 'Admin comment')}</span>
                                                    </button>
                                                  </TooltipTrigger>
                                                  <TooltipContent className="max-w-sm text-left">
                                                    <div className="space-y-2">
                                                      <p className="text-xs font-semibold">
                                                        {t('orders.adminComment.tooltipTitle', 'Administrator comments about your image')}
                                                      </p>
                                                      {itemAdminComments.map((comment, commentIndex) => (
                                                        <p key={comment.itemId + ':' + comment.requestedAt + ':' + String(commentIndex)} className="text-xs leading-relaxed">
                                                          {comment.message}
                                                        </p>
                                                      ))}
                                                    </div>
                                                  </TooltipContent>
                                                </Tooltip>
                                              </TooltipProvider>
                                            ) : null}
                                            {canReupload ? (
                                              <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                className="w-fit"
                                                disabled={isUploadingReplacement}
                                                onClick={() => handleRequestReupload(order.id, item.id)}
                                              >
                                                {isUploadingReplacement
                                                  ? t('orders.reupload.uploading', 'Uploading...')
                                                  : t('orders.reupload.button', 'Re-upload image')}
                                              </Button>
                                            ) : null}
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div className="p-6 text-center text-destructive">
                            {t('orders.errorDetails', 'Could not load order details.')}
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                </section>
              );
            })
          )}
          {showLoadingOverlay ? (
            <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-background/80">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                {t('orders.loading', 'Loading orders...')}
              </div>
            </div>
          ) : null}
        </div>
        {showEmpty ? (
          <div className="rounded-lg border border-dashed p-6 text-center text-muted-foreground">
            {t('orders.empty', 'No orders found for the selected statuses yet.')}
          </div>
        ) : null}
        {nextCursor ? (
          <div className="flex justify-center">
            <Button onClick={handleLoadMore} disabled={isLoadingMore} variant="outline">
              {isLoadingMore ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin" />
                  {t('orders.loadingMore', 'Loading more...')}
                </span>
              ) : (
                t('orders.loadMore', 'Load more')
              )}
            </Button>
          </div>
        ) : null}
      </div>
      {previewImageUrl ? (
        <ImageZoomModal
          isOpen={isPreviewOpen}
          onClose={closePreview}
          imageUrl={previewImageUrl}
          widthIn={previewMeta?.widthIn ?? null}
          heightIn={previewMeta?.heightIn ?? null}
          marginIn={previewMeta?.marginIn ?? null}
          galleryWrapIn={previewMeta?.galleryWrapIn ?? null}
          originalWidthPx={previewMeta?.originalWidthPx ?? null}
          originalHeightPx={previewMeta?.originalHeightPx ?? null}
        />
      ) : null}
      <UkrposhtaTrackingDialog
        open={trackingDialogOrder != null}
        orderId={trackingDialogOrder?.orderId ?? null}
        onClose={closeTrackingDialog}
      />
      <Dialog.Root
        open={isOrderIdDialogOpen}
        onOpenChange={(open) => (open ? null : closeOrderIdDialog())}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[90vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card p-4 text-card-foreground shadow-lg focus:outline-none">
            <div className="space-y-4">
              <Dialog.Title className="text-lg font-semibold">
                {t('orders.dialog.orderIdTitle', 'Order ID')}
              </Dialog.Title>
              <p className="break-all text-sm text-muted-foreground">{selectedOrderId}</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={handleCopyOrderId} disabled={!selectedOrderId}>
                  {t('orders.dialog.copyId', 'Copy ID')}
                </Button>
                <Dialog.Close asChild>
                  <Button variant="default">{t('common.close', 'Close')}</Button>
                </Dialog.Close>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <Dialog.Root
        open={Boolean(orderIdPendingDeletion)}
        onOpenChange={(open) => {
          if (!open) {
            closeDeleteDialog();
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[90vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card p-4 text-card-foreground shadow-lg focus:outline-none">
            <Dialog.Title className="text-lg font-semibold">
              {t('orders.dialog.deleteTitle', 'Delete order')}
            </Dialog.Title>
            <Dialog.Description className="mt-2 text-sm text-muted-foreground">
              {t('orders.dialog.deleteDescription', {
                defaultValue: 'Are you sure you want to delete order {{id}}? This action cannot be undone.',
                id: orderIdPendingDeletion ?? '',
              })}
            </Dialog.Description>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={closeDeleteDialog} disabled={isDeleting}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleting}>
                {isDeleting ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="size-4 animate-spin" />
                    {t('orders.dialog.deleting', 'Deleting...')}
                  </span>
                ) : (
                  t('orders.dialog.confirmDelete', 'Delete')
                )}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
};
